import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { PanelLeft } from "lucide-react";
import { SidebarTrigger, SidebarProvider, useSidebar } from "./components/ui5";
import { AppSidebar } from "./components/app-sidebar";
import AutoBreadcrumb from "./components/AutoBreadcrumb";
import { CustomerSwitcher } from "./components/features/customer/CustomerSwitcher";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Import authentication components
import { AuthProvider } from "./contexts/AuthContext";
import { CustomerProvider } from "./contexts/CustomerContext";
import { ToastProvider } from "./contexts/ToastContext";
import ProtectedRoute from "./components/ProtectedRoute";
import CustomerSelectionGuard from "./guards/CustomerSelectionGuard";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";

// Import all page components
import Dashboard from "./pages/Dashboard";
import ServiceCatalog from "./pages/ServiceCatalog";
import ServiceUsage from "./pages/ServiceUsage";
import ChangeImpact from "./pages/ChangeImpact";
import WhatsNew from "./pages/WhatsNew";
import GenerateReport from "./pages/GenerateReport";
import CustomerSelection from "./pages/CustomerSelection";
import UserManagement from "./pages/UserManagement";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
      gcTime: 1000 * 60 * 10, // Keep unused data in cache for 10 minutes
      refetchOnWindowFocus: true, // Refetch on window focus
      refetchOnMount: true, // Always fetch fresh data on mount
      retry: 1, // Retry failed requests once
    },
  },
});

// Component for authenticated layout
const AuthenticatedLayout = ({ children, showSidebar = true }) => {
  const { toggleSidebar } = useSidebar();

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        width: "100vw",
        backgroundColor: "#f8fafc",
        fontFamily: "72, Arial, Helvetica, sans-serif",
        overflow: "hidden",
      }}
    >
      {showSidebar && <AppSidebar />}
      <main
        style={{
          flex: "1",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100vh",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.75rem 1.5rem",
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e5e5e5",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            minHeight: "4rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {showSidebar && (
              <SidebarTrigger
                onClick={toggleSidebar}
                style={{
                  padding: "0.5rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #e5e5e5",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PanelLeft size={20} />
              </SidebarTrigger>
            )}
            <AutoBreadcrumb />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <CustomerSwitcher />
          </div>
        </header>
        <div
          style={{
            flex: "1",
            overflow: "auto",
            backgroundColor: "#f8fafc",
            height: "calc(100vh - 4rem)",
            width: "100%",
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CustomerProvider>
          <ToastProvider>
            <Router>
              <Routes>
                {/* Public routes - NO SidebarProvider */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />

                {/* Customer selection route */}
                <Route
                  path="/select-customer"
                  element={
                    <ProtectedRoute>
                      <SidebarProvider>
                        <AuthenticatedLayout showSidebar={false}>
                          <CustomerSelection />
                        </AuthenticatedLayout>
                      </SidebarProvider>
                    </ProtectedRoute>
                  }
                />

                {/* Protected routes with customer context */}
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <CustomerSelectionGuard>
                        <SidebarProvider>
                          <AuthenticatedLayout>
                            <Dashboard />
                          </AuthenticatedLayout>
                        </SidebarProvider>
                      </CustomerSelectionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-catalog"
                  element={
                    <ProtectedRoute>
                      <CustomerSelectionGuard>
                        <SidebarProvider>
                          <AuthenticatedLayout>
                            <ServiceCatalog />
                          </AuthenticatedLayout>
                        </SidebarProvider>
                      </CustomerSelectionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-usage"
                  element={
                    <ProtectedRoute>
                      <CustomerSelectionGuard>
                        <SidebarProvider>
                          <AuthenticatedLayout>
                            <ServiceUsage />
                          </AuthenticatedLayout>
                        </SidebarProvider>
                      </CustomerSelectionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/change-impact"
                  element={
                    <ProtectedRoute>
                      <CustomerSelectionGuard>
                        <SidebarProvider>
                          <AuthenticatedLayout>
                            <ChangeImpact />
                          </AuthenticatedLayout>
                        </SidebarProvider>
                      </CustomerSelectionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whats-new"
                  element={
                    <ProtectedRoute>
                      <CustomerSelectionGuard>
                        <SidebarProvider>
                          <AuthenticatedLayout>
                            <WhatsNew />
                          </AuthenticatedLayout>
                        </SidebarProvider>
                      </CustomerSelectionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/generate-report"
                  element={
                    <ProtectedRoute>
                      <CustomerSelectionGuard>
                        <SidebarProvider>
                          <AuthenticatedLayout>
                            <GenerateReport />
                          </AuthenticatedLayout>
                        </SidebarProvider>
                      </CustomerSelectionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <SidebarProvider>
                        <AuthenticatedLayout>
                          <UserManagement />
                        </AuthenticatedLayout>
                      </SidebarProvider>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </ToastProvider>
        </CustomerProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
